import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Key, Calendar, ArrowLeft, CheckCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import apiService from '../services/api';
import toast from 'react-hot-toast';

interface ForgotPasswordForm {
  email: string;
}

const ForgotPasswordPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get login type from URL params
  const searchParams = new URLSearchParams(location.search);
  const loginType = searchParams.get('type') as 'customer' | 'business' | 'professional' | null;

  // Build login URL with type if present
  const getLoginUrl = () => {
    return loginType ? `/login?type=${loginType}` : '/login';
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ForgotPasswordForm>();

  const onSubmit = async (data: ForgotPasswordForm) => {
    setIsLoading(true);
    try {
      const normalizedEmail = data.email.trim().toLowerCase();
      await apiService.forgotPassword(normalizedEmail, loginType || undefined);
      toast.success('Password reset email sent successfully!');
      setIsEmailSent(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = () => {
    setIsEmailSent(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col no-animations">
      {/* Customer Navbar */}
      <CustomerNavbar 
        onLogout={handleLogout}
        showDashboardLink={false}
        showLogoutButton={false}
        showAuthButtons={true}
      />
      
      {/* Main Content - Vertically Centered */}
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      
      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center">
              <Key className="h-10 w-10 text-blue-900" strokeWidth={2} />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            {isEmailSent ? 'Check your email' : 'Forgot your password?'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {isEmailSent 
              ? 'We\'ve sent you a password reset link'
              : 'Enter your email address and we\'ll send you a link to reset your password'
            }
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
          {!isEmailSent ? (
            // Forgot Password Form
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  className={`aacore-textfield-primary ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Enter your email address"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                    onChange: (e) => {
                      const value = e.target.value.toLowerCase();
                      e.target.value = value;
                      setValue('email', value, { shouldValidate: false });
                    },
                  })}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="aacore-btn-primary w-full justify-center"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending reset email...
                  </div>
                ) : (
                  'Send reset email'
                )}
              </button>
            </form>
          ) : (
            // Success State
            <div className="space-y-6">
              <div className="text-center">
                <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100 mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Email sent successfully!
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  We've sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.
                </p>
                
                {/* Resend Email Button */}
                <button
                  onClick={handleResendEmail}
                  className="aacore-btn-primary w-full justify-center"
                >
                  Send another email
                </button>
              </div>
            </div>
          )}

          {/* Back to Login Link */}
          <div className="mt-6 text-center">
            <Link
              to={getLoginUrl()}
              className="inline-flex items-center text-sm font-medium text-blue-900 hover:text-blue-800"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to sign in
            </Link>
          </div>

          {/* Additional Help */}
          {!isEmailSent && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="font-medium text-blue-900 hover:text-blue-800"
                >
                  Sign up here
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ForgotPasswordPage; 